import { Bet } from 'types/Bet';
import { Odd } from 'types/Odd';

/**
 * One market shown as one card per line, the way betbigwin and forzza show
 * them.
 *
 * The feed packs every line of a bet into one market: "Alternative Handicap
 * Result" carries -3, -2, +1, +2 ... for all three outcomes, and "Alternative
 * Total Goals" carries 0.5, 1.5, 3.5 ... over and under. Listed as one block,
 * the picks read "-1 (1)", "+1 (Nerešeno)", "2.5 (Preko)", which the client
 * could not follow (2026-10-20 recording). Here each line becomes its own card
 * titled with the line - "Hendikep (0:1)", "Više/manje golova (2.5)" - and its
 * picks get short names: 1 / X / 2, Preko / Ispod, 1X / 12 / X2.
 *
 * Only what is shown changes. A card keeps the market's name and id and the
 * feed's own odds objects, so the slip and settlement see exactly what they
 * saw before.
 */

export type MarketCard = Bet & {
  /** Said after the market's name: "(0:1)", "(2.5)", "Fenerbahce (1.5)". */
  line?: string;
  /** Short pick names, by odd id; untranslated ("Over", "1X"). */
  pickLabels?: Record<number, string>;
  /** Set when every pick on the card is about one team. */
  team?: 'home' | 'away';
  /** The part of a lines market the card shows (see linesPart). */
  part?: LinesPart;
};

/**
 * A lines market ("Game Lines", "1st Half", "Match Lines") bundles who wins,
 * the handicap and the total. Betbigwin shows them in three sections (main
 * odds, Handicap, Over/Under), so they are three cards here, each placed on
 * its own ("Game Lines#handicap" in the offer layout).
 */
export type LinesPart = 'winner' | 'handicap' | 'total';

const LINES_SIDE = /^(1|2|x|tie\b.*|draw)$/i;
const LINES_PART_WORDS: Record<string, LinesPart> = {
  'money line': 'winner',
  'to win': 'winner',
  winner: 'winner',
  result: 'winner',
  spread: 'handicap',
  line: 'handicap',
  'run line': 'handicap',
  handicap: 'handicap',
  'match handicap': 'handicap',
  'puck line': 'handicap',
  total: 'total',
  'total maps': 'total',
  'total points': 'total',
  'total games': 'total',
};

/**
 * The part of a lines market a pick belongs to, or null for a pick of any
 * other market. The feed puts the part in the header and the side in the
 * name, or the other way round. The API reads picks the same way when a part
 * is switched off (bets.LinesPart) - keep the two in step.
 */
export function linesPart(odd: Pick<Odd, 'header' | 'name'>): LinesPart | null {
  const header = text(odd.header);
  const name = text(odd.name);
  for (const [part, side] of [
    [header, name],
    [name, header],
  ]) {
    const kind = LINES_PART_WORDS[part.toLowerCase()];
    if (kind && LINES_SIDE.test(side)) return kind;
  }
  return null;
}

/** The side of a lines pick: 1, X or 2. */
const linesSideOf = (odd: Odd) => {
  const side = LINES_SIDE.test(text(odd.name))
    ? text(odd.name)
    : text(odd.header);
  return /^(1|2)$/.test(side) ? side : 'X';
};

/** A lines market as one card per part, in winner, handicap, total order. */
function linesParts(bet: Bet): MarketCard[] | null {
  const odds = bet.odds ?? [];
  const parts = odds.map((o) => linesPart(o));
  if (!odds.length || parts.some((p) => p == null)) return null;
  const order: LinesPart[] = ['winner', 'handicap', 'total'];
  return order
    .filter((part) => parts.includes(part))
    .map((part) => {
      const group = odds.filter((_, i) => parts[i] === part);
      const labels: Record<number, string> = {};
      let line: string | undefined;
      for (const o of group) {
        const side = linesSideOf(o);
        const h = text(o.handicap);
        if (part === 'total') {
          const m = /^([OU])\w*\s*(.+)$/i.exec(h);
          labels[o.custom_id] =
            m && m[1].toUpperCase() === 'U' ? 'Under' : 'Over';
          if (m) line = `(${m[2]})`;
        } else {
          labels[o.custom_id] =
            part === 'handicap' && h ? `${side} (${h})` : side;
        }
      }
      return { ...bet, odds: group, part, line, pickLabels: labels };
    });
}

/** Where a card is placed in the offer layout: its market, or for a lines
 *  market's part "Game Lines#handicap". */
export const layoutKeyOf = (card: {
  name?: string | null;
  part?: LinesPart;
}) => (card.part ? `${text(card.name)}#${card.part}` : text(card.name));

type Teams = { home?: string | null; away?: string | null };

const text = (v: unknown) => (v == null ? '' : String(v).trim());
const OVER_UNDER = new Set(['over', 'under', 'exactly']);
const LINE = /^[+-]?\d+(\.\d+)?(\s*,\s*[+-]?\d+(\.\d+)?)?$/;

/** "-0.5,-1.0" -> "+0.5, +1.0": the same line seen from the other side. */
const flipLine = (line: string) =>
  line
    .split(',')
    .map((part) => {
      const bare = part.trim().replace(/^[+-]/, '');
      const n = Number(part.trim());
      // 0 and 0.0 have no side; keep the feed's spelling so both halves of
      // the pair meet on the same line.
      if (n === 0) return bare;
      return (n > 0 ? '-' : '+') + bare;
    })
    .join(', ');

const tidyLine = (line: string) =>
  line
    .split(',')
    .map((p) => p.trim())
    .join(', ');

/** Cards in the order their lines first appear in the feed. */
function byKey(
  bet: Bet,
  keyOf: (odd: Odd) => string | null,
  card: (key: string, odds: Odd[]) => MarketCard
): MarketCard[] | null {
  const odds = bet.odds ?? [];
  const groups = new Map<string, Odd[]>();
  for (const odd of odds) {
    const key = keyOf(odd);
    if (key == null) return null;
    groups.set(key, [...(groups.get(key) ?? []), odd]);
  }
  return Array.from(groups.entries()).map(([key, group]) => card(key, group));
}

/** The home side's handicap in a 1 / X / 2 handicap: home -1 is "0:1". */
const scoreLine = (homeHandicap: number) =>
  homeHandicap < 0 ? `(0:${-homeHandicap})` : `(${homeHandicap}:0)`;

const homeHandicapOf = (o: Odd) => {
  const h = Number(text(o.handicap));
  return text(o.header) === '1' ? h : -h;
};

/**
 * A pick of a 1 / X / 2 handicap as the slip and the ticket show it: the line
 * as a head start ("(0:1)") and the pick as 1, X or 2. The feed stores the
 * same pick as "+1 (Tie)" - each outcome's own handicap - which players read
 * wrong. Null for any other market (Asian and 2-way handicaps keep their line).
 */
export function europeanHandicapPick(
  marketName: string | null | undefined,
  odd: Pick<Odd, 'header' | 'handicap'>
): { line: string; pick: '1' | 'X' | '2' } | null {
  const name = text(marketName).toLowerCase();
  if (!/handicap/.test(name) || /asian|2-way|2 way/.test(name)) return null;
  const header = text(odd.header);
  if (!['1', 'Tie', '2'].includes(header)) return null;
  if (!/^[+-]?\d+$/.test(text(odd.handicap))) return null;
  return {
    line: scoreLine(homeHandicapOf(odd as Odd)),
    pick: header === 'Tie' ? 'X' : (header as '1' | '2'),
  };
}

/** 1 / X / 2 European handicap: the home side's handicap names the line. */
function threeWayHandicap(bet: Bet): MarketCard[] | null {
  const odds = bet.odds ?? [];
  const isThreeWay =
    odds.some((o) => text(o.header) === 'Tie') &&
    odds.every(
      (o) =>
        ['1', 'Tie', '2'].includes(text(o.header)) &&
        /^[+-]?\d+$/.test(text(o.handicap))
    );
  if (!isThreeWay) return null;
  const homeLine = homeHandicapOf;
  const cards = byKey(
    bet,
    (o) => String(homeLine(o)),
    (key, group) => {
      const h = Number(key);
      return {
        ...bet,
        odds: group,
        // Home -1 means the away side starts a goal up: 0:1.
        line: scoreLine(h),
        pickLabels: Object.fromEntries(
          group.map((o) => [
            o.custom_id,
            text(o.header) === 'Tie' ? 'X' : text(o.header),
          ])
        ),
      };
    }
  );
  // 0:1, 0:2, 0:3, then 1:0, 2:0 - as both reference books list them.
  const rank = (c: MarketCard) => {
    const h = homeLine(c.odds![0]);
    return h < 0 ? -h : 100 + h;
  };
  return cards && cards.sort((a, b) => rank(a) - rank(b));
}

/** 1 / 2 handicaps (2-way, Asian): the home side's line names the card. */
function twoWayHandicap(bet: Bet): MarketCard[] | null {
  const odds = bet.odds ?? [];
  const isTwoWay = odds.every(
    (o) => ['1', '2'].includes(text(o.header)) && LINE.test(text(o.handicap))
  );
  if (!isTwoWay || !odds.length) return null;
  const cards = byKey(
    bet,
    (o) =>
      text(o.header) === '1'
        ? tidyLine(text(o.handicap))
        : flipLine(text(o.handicap)),
    (key, group) => ({
      ...bet,
      odds: group,
      line: `(${key})`,
      pickLabels: Object.fromEntries(
        group.map((o) => [o.custom_id, text(o.header)])
      ),
    })
  );
  // -2.5, -1.5, -0.5, +0.5 ... rather than the feed's order.
  return (
    cards &&
    cards.sort(
      (a, b) => parseFloat(a.line!.slice(1)) - parseFloat(b.line!.slice(1))
    )
  );
}

/** Over / under a line given in the pick's name: "Over 2.5". */
function overUnder(bet: Bet): MarketCard[] | null {
  const odds = bet.odds ?? [];
  const lineOf = (o: Odd) =>
    LINE.test(text(o.name)) ? text(o.name) : text(o.handicap);
  const isOverUnder =
    odds.length > 0 &&
    odds.every(
      (o) =>
        OVER_UNDER.has(text(o.header).toLowerCase()) && LINE.test(lineOf(o))
    );
  if (!isOverUnder) return null;
  return byKey(
    bet,
    (o) => tidyLine(lineOf(o)),
    (key, group) => ({
      ...bet,
      odds: group,
      line: `(${key})`,
      pickLabels: Object.fromEntries(
        group.map((o) => [o.custom_id, text(o.header)])
      ),
    })
  );
}

/** One team's over / under: header 1 or 2, "Over 1.5" in the handicap. */
function teamOverUnder(bet: Bet, teams: Teams): MarketCard[] | null {
  const odds = bet.odds ?? [];
  const parse = (o: Odd) =>
    /^(Over|Under)\s+(\d+(\.\d+)?)$/i.exec(text(o.handicap));
  const isTeamLine =
    odds.length > 0 &&
    odds.every((o) => ['1', '2'].includes(text(o.header)) && parse(o));
  if (!isTeamLine) return null;
  return byKey(
    bet,
    (o) => `${text(o.header)}|${parse(o)![2]}`,
    (key, group) => {
      const [side, line] = key.split('|');
      const team = side === '1' ? 'home' : 'away';
      return {
        ...bet,
        odds: group,
        team,
        line: `${text(teams[team]) || side} (${line})`,
        pickLabels: Object.fromEntries(
          group.map((o) => [o.custom_id, parse(o)![1]])
        ),
      };
    }
  );
}

/** Picks about one team or the other (clean sheet, team goal ranges). */
function perTeam(bet: Bet, teams: Teams): MarketCard[] | null {
  const odds = bet.odds ?? [];
  const isPerTeam =
    odds.length > 1 &&
    odds.every((o) => ['1', '2'].includes(text(o.header))) &&
    odds.some((o) => text(o.header) === '1') &&
    odds.some((o) => text(o.header) === '2') &&
    // A plain 1 / 2 market (draw no bet) is one bet, not two teams'.
    odds.some((o) => text(o.name) || text(o.handicap));
  if (!isPerTeam) return null;
  return byKey(
    bet,
    (o) => text(o.header),
    (side, group) => {
      const team = side === '1' ? 'home' : 'away';
      return { ...bet, odds: group, team, line: text(teams[team]) || side };
    }
  );
}

/**
 * Markets that name a pick the same way for both teams: "To Score In Half"
 * sends "1st Half / Yes" twice, the home side's first. The team is known only
 * from the order (the API stores it the same way, see teamOfPositionalPick).
 */
export const POSITIONAL_TEAM_MARKETS = new Set(['to score in half']);

/** '1' (home) or '2' (away) for a pick of a POSITIONAL_TEAM_MARKETS market. */
export function positionalSide(
  odds: Odd[],
  odd: Pick<Odd, 'custom_id' | 'name' | 'header'>
): '1' | '2' | null {
  const twins = odds
    .filter(
      (o) =>
        text(o.name) === text(odd.name) && text(o.header) === text(odd.header)
    )
    .map((o) => Number(o.custom_id))
    .sort((a, b) => a - b);
  if (twins.length !== 2) return null;
  const i = twins.indexOf(Number(odd.custom_id));
  return i === 0 ? '1' : i === 1 ? '2' : null;
}

function positionalPerTeam(bet: Bet, teams: Teams): MarketCard[] | null {
  const odds = bet.odds ?? [];
  return byKey(
    bet,
    (o) => positionalSide(odds, o),
    (side, group) => {
      const team = side === '1' ? 'home' : 'away';
      return { ...bet, odds: group, team, line: text(teams[team]) || side };
    }
  );
}

/** Markets whose picks are each about one team, by the feed's name. */
const PER_TEAM_MARKETS = new Set(
  [
    'Clean Sheet',
    'Team Goals Range',
    'Time of 1st Team Goal',
    'Team Performances',
  ].map((n) => n.toLowerCase())
);

/** Short names for the picks of the plain result markets. */
function shortResultPicks(bet: Bet, teams: Teams): MarketCard {
  const home = text(teams.home).toLowerCase();
  const away = text(teams.away).toLowerCase();
  const side = (s: string) => {
    const v = s.trim().toLowerCase();
    if (v === 'draw' || v === 'tie') return 'X';
    if (v === home || v === '1') return '1';
    if (v === away || v === '2') return '2';
    return null;
  };
  const labels: Record<number, string> = {};
  for (const o of bet.odds ?? []) {
    const name = text(o.name);
    const or = name.split(/\s+or\s+/i);
    const ht = name.split(/\s+-\s+/);
    let label: string | null = null;
    if (or.length === 2 && side(or[0]) && side(or[1])) {
      label = [side(or[0])!, side(or[1])!].sort(byPick).join('');
    } else if (ht.length === 2 && side(ht[0]) && side(ht[1])) {
      label = `${side(ht[0])}/${side(ht[1])}`;
    } else if (!text(o.header) && side(name)) {
      label = side(name);
    }
    if (label) labels[o.custom_id] = label;
  }
  if (Object.keys(labels).length !== (bet.odds ?? []).length) return bet;
  // Double chance reads 1X, 12, X2 (the feed sends 1X, X2, 12).
  const DC = ['1X', '12', 'X2'];
  const odds = [...(bet.odds ?? [])];
  if (odds.every((o) => DC.includes(labels[o.custom_id])))
    odds.sort(
      (a, b) =>
        DC.indexOf(labels[a.custom_id]) - DC.indexOf(labels[b.custom_id])
    );
  return { ...bet, odds, pickLabels: labels };
}

// 1X, X2, 12 - never X1 or 21.
const byPick = (a: string, b: string) =>
  ['1', 'X', '2'].indexOf(a) - ['1', 'X', '2'].indexOf(b);

const SHORT_PICK_MARKETS = new Set(
  [
    'Full Time',
    'Full Time Result',
    'Double Chance',
    'Half Time Double Chance',
    'Half Time Result',
    '2nd Half Result',
    'Half Time/Full Time',
    'Draw No Bet',
    'First Team to Score',
    'Last Team to Score',
    '10 Minute Result',
  ].map((n) => n.toLowerCase())
);

/** Picks in the order they read on betbigwin, with their short names. */
function sortedPicks(
  bet: Bet,
  rankAndLabel: (o: Odd) => [number, string] | null
): MarketCard | null {
  const odds = bet.odds ?? [];
  const ranked = odds.map((o) => ({ o, rl: rankAndLabel(o) }));
  if (!ranked.every((r) => r.rl)) return null;
  ranked.sort((a, b) => a.rl![0] - b.rl![0]);
  return {
    ...bet,
    odds: ranked.map((r) => r.o),
    pickLabels: Object.fromEntries(
      ranked.map((r) => [r.o.custom_id, r.rl![1]])
    ),
  };
}

const RESULT_PICK = (name: string): '1' | 'X' | '2' | null => {
  const v = name.trim().toLowerCase();
  if (v === '1') return '1';
  if (v === '2') return '2';
  if (v === 'draw' || v === 'tie' || v === 'x') return 'X';
  return null;
};
const PICK_ORDER = { '1': 0, X: 1, '2': 2 } as const;

/**
 * Result + both teams to score: "1 & GG", "X & GG", "2 & GG", then the NG
 * row. The feed gives the result in the name and Yes / No in the header,
 * listed 1, 2, X.
 */
function resultAndBtts(bet: Bet): MarketCard | null {
  return sortedPicks(bet, (o) => {
    const pick = RESULT_PICK(text(o.name));
    const yes = text(o.header).toLowerCase();
    if (!pick || (yes !== 'yes' && yes !== 'no')) return null;
    const gg = yes === 'yes' ? 'GG' : 'NG';
    return [(yes === 'yes' ? 0 : 10) + PICK_ORDER[pick], `${pick} & ${gg}`];
  });
}

/**
 * Total + both teams to score: "Over 2.5 & Yes" reads "GG & 3+", "Under 2.5
 * & No" "NG & 0-2" - GG & 3+, GG & 0-2, NG & 3+, NG & 0-2.
 */
function totalAndBtts(bet: Bet): MarketCard | null {
  return sortedPicks(bet, (o) => {
    const m = /^(Over|Under)\s+(\d+)\.5\s*&\s*(Yes|No)$/i.exec(text(o.name));
    if (!m) return null;
    const goals = Number(m[2]);
    const over = m[1].toLowerCase() === 'over';
    const gg = m[3].toLowerCase() === 'yes';
    return [
      (gg ? 0 : 10) + (over ? 0 : 1),
      `${gg ? 'GG' : 'NG'} & ${over ? `${goals + 1}+` : `0-${goals}`}`,
    ];
  });
}

/**
 * A short pick name as shown: Over / Under / Exactly in the player's language,
 * the rest ("1X", "1 & GG", "GG & 3+") as it is.
 */
export function pickLabelText(label: string, translate: (s: string) => string) {
  if (/^(Over|Under|Exactly|Yes|No)$/.test(label)) return translate(label);
  // "1 & Over 2.5", "I Over 0.5 & II Over 1.5": the lines' words translated.
  if (/\b(Over|Under) \d/.test(label))
    return label.replace(/\b(Over|Under)(?= \d)/g, (w) => translate(w));
  // "1 & Yes", "1X & No": a result with a translated Yes / No.
  const m = /^(.+) & (Yes|No)$/.exec(label);
  return m ? `${m[1]} & ${translate(m[2])}` : label;
}

/**
 * Goal-range markets ("Match Goals Range", "Results/Goals Range" ...): one
 * card per range, which the sync stores as the handicap ("1-2"); the team
 * range also per team. Picks: Yes / No, or the result with it ("1 & Yes").
 */
function goalsRange(bet: Bet, teams: Teams): MarketCard[] | null {
  const odds = bet.odds ?? [];
  if (!odds.every((o) => /^\d+(-\d+|\+)$/.test(text(o.handicap)))) return null;
  const perTeam = /^team goals range$/i.test(text(bet.name));
  const DC = ['1X', '12', 'X2'];
  return byKey(
    bet,
    (o) => (perTeam ? `${text(o.name)}|${text(o.handicap)}` : text(o.handicap)),
    (key, group) => {
      const [side, range] = perTeam ? key.split('|') : ['', key];
      const team = side === '1' ? 'home' : side === '2' ? 'away' : undefined;
      const result = shortResultPicks({ ...bet, odds: group }, teams);
      // The team range names the team "1" / "2", not a result.
      const resultOf = (o: Odd) =>
        perTeam
          ? undefined
          : RESULT_PICK(text(o.name)) ?? result.pickLabels?.[o.custom_id];
      const rank = (o: Odd) => {
        const r = resultOf(o);
        const yes = text(o.header).toLowerCase() === 'yes' ? 0 : 10;
        const pos = r ? Math.max(DC.indexOf(r), ['1', 'X', '2'].indexOf(r)) : 0;
        return yes + pos;
      };
      const sorted = [...group].sort((a, b) => rank(a) - rank(b));
      return {
        ...bet,
        odds: sorted,
        team,
        line: team ? `${text(teams[team]) || side} ${range}` : range,
        pickLabels: Object.fromEntries(
          sorted.map((o) => {
            const yesNo = text(o.header);
            const r = resultOf(o);
            return [o.custom_id, r ? `${r} & ${yesNo}` : yesNo];
          })
        ),
      };
    }
  );
}

/**
 * Correct score: "3-1 (1)" names the winner by its side, which players had to
 * work out; the team's name reads at once - "3-1 (Arsenal)". Draws keep the
 * feed's pick ("1-1 (Nerešeno)"). Prematch odds carry the side in the header,
 * live ones in the handicap.
 */
export function correctScore(
  bet: Pick<Bet, 'odds'> & Partial<Bet>,
  teams: Teams
): MarketCard | null {
  const odds = bet.odds ?? [];
  const sideOf = (o: Odd) => text(o.header) || text(o.handicap);
  const isScore =
    odds.length > 0 &&
    odds.every(
      (o) =>
        /^\d+-\d+$/.test(text(o.name)) &&
        ['1', '2', 'x', 'draw', 'tie'].includes(sideOf(o).toLowerCase())
    );
  if (!isScore) return null;
  const labels: Record<number, string> = {};
  for (const o of odds) {
    const side = sideOf(o);
    const team = side === '1' ? teams.home : side === '2' ? teams.away : null;
    if (text(team)) labels[o.custom_id] = `${text(o.name)} (${text(team)})`;
  }
  return Object.keys(labels).length
    ? ({ ...bet, pickLabels: labels } as MarketCard)
    : null;
}

/**
 * Result or double chance with goals, written as Mozzart writes it and in
 * one card (owner, 2026-11-02): "1&3+" (1 and over 2.5), "1&0-2" (under
 * 2.5), "1X&2-4" (a goals range), "1p&2+" (half time result, first half
 * goals). bet365 sent them as one card per range or line with "1 & Yes",
 * and "Result / Total Goals" read as "1X2 + over/under (1)".
 */
const RESULT_GOALS_MARKETS = new Set(
  [
    'Result / Total Goals',
    // Tottalo's lines of the same bet ("1&4+", "X&0-2").
    'Result & Goals',
    'Half Time Result / Total Goals',
    'Double Chance & Total Goals',
    'Results/Goals Range',
    'Double Chance/Goals Range',
    'Result & Multi Goals',
    'Double Chance & Multi Goals',
  ].map((n) => n.toLowerCase())
);

/** Over 2.5 -> "3+", Under 2.5 -> "0-2". */
const goalsLabel = (overUnder: string, line: string): string | null => {
  const l = Number(line);
  if (!Number.isFinite(l) || Math.abs((l % 1) - 0.5) > 1e-9) return null;
  const n = Math.floor(l);
  if (/^over$/i.test(overUnder)) return `${n + 1}+`;
  if (/^under$/i.test(overUnder)) return `0-${n}`;
  return null;
};

/**
 * A goals part as the over / under line, the way the totals read (owner,
 * 2026-11-04: "3+" in one place and "2.5" in another confused players):
 * "3+" -> "Over 2.5", "0-2" -> "Under 2.5". A range ("2-3") stays.
 */
export const goalsAsLine = (g: string): string => {
  const plus = /^(\d+)\+$/.exec(g);
  if (plus) return `Over ${Number(plus[1]) - 0.5}`;
  const upTo = /^0-(\d+)$/.exec(g);
  if (upTo) return `Under ${Number(upTo[1]) + 0.5}`;
  return g;
};

/** Tottalo's own combination markets (tottalo_api computed_combos.go,
 *  computed_htft.go): picks named Mozzart's way, "1-1&3+", "I1+&II2+". */
const COMBO_MARKETS = new Set(
  [
    'Goals in Each Half',
    'Result & Half Goals',
    'Result & Half With More Goals',
    'Double Chance & Half With More Goals',
    'Double Chance & 1st Half Goals',
    'Half Time/Full Time & Goals',
    'Half Time/Full Time Combos',
    'Half Time/Full Time & 3+ Goals',
  ].map((n) => n.toLowerCase())
);

/**
 * A combination pick with its goals parts as totals lines (owner,
 * 2026-11-04): "1-1&3+" -> "1-1 & Over 2.5", "I1+&II2+" -> "I Over 0.5 &
 * II Over 1.5", "1&T1 3+" -> "1 & T1 Over 2.5". The result part, ranges
 * ("2-3") and words (GG, vg1) stay as they are.
 */
export function comboAsLines(name: string): string {
  return name
    .split('&')
    .map((part, i) => {
      const p = part.trim();
      // The half time / full time result ("1-1", "X-2") leads the pick.
      if (i === 0 && /^[12X]-[12X]$/.test(p)) return p;
      const m = /^(I{1,2}|T[12])?\s*(\d+\+|0-\d+)$/.exec(p);
      if (!m) return p;
      const line = goalsAsLine(m[2]);
      return m[1] ? `${m[1]} ${line}` : line;
    })
    .join(' & ');
}

/** The picks of a Tottalo combination market as totals lines. */
function comboLines(bet: Bet): MarketCard | null {
  const odds = bet.odds ?? [];
  if (!odds.length) return null;
  return {
    ...bet,
    pickLabels: Object.fromEntries(
      odds.map((o) => [o.custom_id, comboAsLines(text(o.name))])
    ),
  };
}

/** Mozzart's order of goals parts: 2+, 3+, 4+, 0-2, 0-3, 2-3 ... 4-6. */
const goalsRank = (g: string): number => {
  const plus = /^(\d+)\+$/.exec(g);
  if (plus) return Number(plus[1]);
  const r = /^(\d+)-(\d+)$/.exec(g);
  if (!r) return 999;
  const [lo, hi] = [Number(r[1]), Number(r[2])];
  return (lo === 0 ? 100 : 200 + lo * 10) + hi;
};

function resultGoals(bet: Bet, teams: Teams): MarketCard | null {
  const name = text(bet.name).toLowerCase();
  const half = name.startsWith('half time');
  const sides = shortResultPicks(bet, teams).pickLabels ?? {};
  const SIDE_ORDER = ['1', 'X', '2', '1X', '12', 'X2'];
  const labelled: { o: Odd; side: string; goals: string }[] = [];
  for (const o of bet.odds ?? []) {
    const n = text(o.name);
    let side: string | null = null;
    let goals: string | null = null;
    const amp = /^(.+?)\s*&\s*(.+)$/.exec(n);
    if (amp) {
      // bwin: "12 & Over" (line in the handicap), "1 & 2-3".
      side = amp[1].replace(/\s+/g, '');
      goals = /^(over|under)$/i.test(amp[2])
        ? goalsLabel(amp[2], text(o.handicap))
        : amp[2].replace(/\s+/g, '');
    } else {
      side = RESULT_PICK(n) ?? sides[o.custom_id] ?? null;
      const header = text(o.header);
      goals = /^(over|under)$/i.test(header)
        ? goalsLabel(header, text(o.handicap))
        : /^yes$/i.test(header)
          ? text(o.handicap)
          : null;
    }
    if (side === 'Draw') side = 'X';
    if (!side || !goals || !SIDE_ORDER.includes(side)) return null;
    labelled.push({ o, side, goals });
  }
  if (!labelled.length) return null;
  labelled.sort(
    (a, b) =>
      SIDE_ORDER.indexOf(a.side) - SIDE_ORDER.indexOf(b.side) ||
      goalsRank(a.goals) - goalsRank(b.goals)
  );
  return {
    ...bet,
    odds: labelled.map((x) => x.o),
    pickLabels: Object.fromEntries(
      labelled.map((x) => [
        x.o.custom_id,
        `${x.side}${half ? 'p' : ''} & ${goalsAsLine(x.goals)}`,
      ])
    ),
  };
}

/** A market as the cards it is shown in, one per line. */
export function splitMarket(bet: Bet, teams: Teams): MarketCard[] {
  if (!bet?.odds?.length) return [bet];
  const name = text(bet.name).toLowerCase();
  if (RESULT_GOALS_MARKETS.has(name)) {
    const card = resultGoals(bet, teams);
    if (card) return [card];
  }
  if (COMBO_MARKETS.has(name)) return [comboLines(bet) ?? bet];
  if (/goals range$/.test(name)) return goalsRange(bet, teams) ?? [bet];
  if (
    /^(result\/both teams to score|half time result\/both teams to score)$/.test(
      name
    )
  )
    return [resultAndBtts(bet) ?? bet];
  if (name === 'total goals/both teams to score')
    return [totalAndBtts(bet) ?? bet];
  if (SHORT_PICK_MARKETS.has(name)) return [shortResultPicks(bet, teams)];
  if (PER_TEAM_MARKETS.has(name)) return perTeam(bet, teams) ?? [bet];
  if (POSITIONAL_TEAM_MARKETS.has(name))
    return positionalPerTeam(bet, teams) ?? [bet];
  const score = correctScore(bet, teams);
  if (score) return [score];
  const lines = linesParts(bet);
  if (lines) return lines;
  return (
    threeWayHandicap(bet) ??
    twoWayHandicap(bet) ??
    overUnder(bet) ??
    teamOverUnder(bet, teams) ?? [bet]
  );
}

/**
 * The main line and the alternative lines of one bet come as two markets
 * ("Goals Over/Under" 2.5, "Alternative Total Goals" 0.5, 1.5, 3.5 ...). Shown
 * under one name, they read best as one run, line by line.
 */
const LINE_FAMILIES = [
  ['Goals Over/Under', 'Alternative Total Goals'],
  ['Handicap Result', 'Alternative Handicap Result'],
  ['1st Half Handicap', 'Alternative 1st Half Handicap Result'],
  ['Asian Handicap', 'Alternative Asian Handicap'],
  ['Goal Line', 'Alternative Goal Line'],
  ['1st Half Asian Handicap', 'Alternative 1st Half Asian Handicap'],
  ['1st Half Goal Line', 'Alternative 1st Half Goal Line'],
  ['Corners', 'Alternative Corners'],
].map((names) => new Set(names.map((n) => n.toLowerCase())));

/** Where a card's line sorts: 0:1, 0:2 ... 1:0, 2:0; 0.5, 1.5, 2.5 ... */
const lineRank = (card: MarketCard): number => {
  const line = card.line ?? '';
  const score = /^\((\d+):(\d+)\)$/.exec(line);
  if (score) {
    const [home, away] = [Number(score[1]), Number(score[2])];
    return home === 0 ? away : 100 + home;
  }
  const n = parseFloat(line.replace(/^\(/, ''));
  return Number.isNaN(n) ? 0 : n;
};

/** Every market of a match as its cards, in the markets' order. */
export function splitMarkets(bets: Bet[], teams: Teams): MarketCard[] {
  const cards = bets.flatMap((bet) => splitMarket(bet, teams));
  for (const family of LINE_FAMILIES) {
    const inFamily = (c: MarketCard) => family.has(text(c.name).toLowerCase());
    const first = cards.findIndex(inFamily);
    if (first < 0) continue;
    const members = cards.filter(inFamily);
    const sorted = [...members].sort((a, b) => lineRank(a) - lineRank(b));
    const rest = cards.filter((c) => !inFamily(c));
    const at = cards.slice(0, first).filter((c) => !inFamily(c)).length;
    cards.splice(
      0,
      cards.length,
      ...rest.slice(0, at),
      ...sorted,
      ...rest.slice(at)
    );
  }
  return cards;
}

/**
 * Live handicaps as forzza.com lists them: one card per line. The live feed
 * names each pick's team (or "Draw") and gives its own handicap, so the
 * picks of a line are found by the home side's handicap - a draw and an
 * away pick carry the away side's, as in prematch. 3-way lines read
 * "(0:1)" with 1 / X / 2, 2-way ones the home side's line with 1 / 2.
 * Asian and corner handicaps stay as they are.
 */
export function splitLiveHandicap(bet: Bet, teams: Teams): MarketCard[] | null {
  const name = text(bet.name).toLowerCase();
  if (!/handicap/.test(name) || /asian|corner/.test(name)) return null;
  const odds = bet.odds ?? [];
  if (!odds.length || !teams.home || !teams.away) return null;
  const sideOf = (o: Odd): '1' | 'X' | '2' | null => {
    const n = text(o.name);
    if (n === teams.home) return '1';
    if (n === teams.away) return '2';
    if (/^(draw|tie)$/i.test(n)) return 'X';
    return null;
  };
  if (odds.some((o) => !sideOf(o) || !LINE.test(text(o.handicap)))) return null;
  const threeWay = odds.some((o) => sideOf(o) === 'X');
  if (threeWay && !odds.every((o) => /^[+-]?\d+$/.test(text(o.handicap))))
    return null;
  const homeLine = (o: Odd) => {
    const h = Number(text(o.handicap));
    return sideOf(o) === '1' ? h : -h;
  };
  const cards = byKey(
    bet,
    (o) => String(homeLine(o)),
    (key, group) => {
      const h = Number(key);
      const order = { '1': 0, X: 1, '2': 2 };
      const sorted = [...group].sort(
        (a, b) => order[sideOf(a)!] - order[sideOf(b)!]
      );
      return {
        ...bet,
        odds: sorted,
        line: threeWay ? scoreLine(h) : `(${h > 0 ? '+' : ''}${h})`,
        pickLabels: Object.fromEntries(
          group.map((o) => [o.custom_id, sideOf(o)!])
        ),
      };
    }
  );
  if (!cards) return null;
  const rank = (c: MarketCard) => {
    const h = homeLine(c.odds![0]);
    return threeWay ? (h < 0 ? -h : 100 + h) : h;
  };
  return cards.sort((a, b) => rank(a) - rank(b));
}

/** Over and Under at several lines in one market (bwin's team goals in a
 *  half): shown two to a row so each line's pair stays together. */
export const overUnderLines = (
  odds: ReadonlyArray<{ name?: unknown; handicap?: unknown }>
) =>
  odds.length >= 2 &&
  odds.length % 2 === 0 &&
  odds.every(
    (o) =>
      /^(over|under)$/i.test(String(o.name ?? '').trim()) &&
      o.handicap != null &&
      String(o.handicap).trim() !== ''
  );
